import React from "react";
import useFrontPageCta from "../../../Hooks/useFrontPageCta";
import { Link } from "react-router-dom";

// Constants
import { RoutePaths } from "../../../Constants/RoutePaths";

// Hooks
import useFetchFrontFeaturedContent, {
    FrontFeaturedContentAvailableItem,
    FrontFeaturedContentItem,
    FrontFeaturedContentUnavailableItem,
    isFrontFeaturedContentAvailable,
} from "../../../Hooks/Home/useFetchFrontFeaturedContent";

const isAbsoluteImage = (value?: string | null): value is string =>
    Boolean(value?.trim() && /^https?:\/\//i.test(value.trim()));

const getArtwork = (item: FrontFeaturedContentAvailableItem): string => {
    const candidates =
        item.kind === "film"
            ? [item.content.Thumbnail, item.content.BackgroundImage]
            : [
                  item.content.HeaderImg,
                  item.content.BackgroundImage,
                  item.content.SeriesCover,
              ];

    return candidates.find(isAbsoluteImage)?.trim() ?? "";
};

const getDescription = (description?: string | null): string => {
    const plainText = (description ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const segments = plainText
        .split(/\s+-\s+/)
        .map((segment) => segment.trim())
        .filter(Boolean);

    return segments[segments.length - 1] ?? "";
};

const getDestination = (item: FrontFeaturedContentAvailableItem): string => {
    if (item.kind === "series") {
        return RoutePaths.SeriesEpisodes(item.content.Id);
    }

    return item.content.TrailerLink?.trim()
        ? RoutePaths.WatchTrailer(item.content.Id)
        : RoutePaths.Watch(item.content.Id);
};

const getCallToAction = (item: FrontFeaturedContentAvailableItem): string => {
    if (item.kind === "series") return "Explore series";
    return item.content.TrailerLink?.trim() ? "Watch preview" : "Watch film";
};

interface OriginalCardProps {
    item: FrontFeaturedContentAvailableItem;
}

const OriginalCard: React.FC<OriginalCardProps> = ({ item }) => {
    const artwork = getArtwork(item);
    const description = getDescription(item.content.Description);
    const title = item.content.Title?.trim() || "Ickonic original";

    return (
        <Link
            className="front-originals__card"
            to={getDestination(item)}
            aria-label={`${getCallToAction(item)}: ${title}`}
        >
            {artwork ? (
                <img
                    className="front-originals__artwork"
                    src={artwork}
                    alt=""
                    loading="lazy"
                />
            ) : null}
            <span className="front-originals__shade" aria-hidden="true" />

            <span className="front-originals__card-copy">
                <span className="front-originals__meta">
                    <span>
                        {item.content.IsOriginal
                            ? "Ickonic original"
                            : "Featured on Ickonic"}
                    </span>
                    <span>{item.kind === "film" ? "Film" : "Series"}</span>
                </span>

                <span className="front-originals__title" role="heading" aria-level={3}>
                    {title}
                </span>

                {description ? (
                    <span className="front-originals__description">
                        {description}
                    </span>
                ) : null}

                <span className="front-originals__cta">
                    {getCallToAction(item)}
                    <span aria-hidden="true">&rarr;</span>
                </span>
            </span>
        </Link>
    );
};

interface UnavailableOriginalCardProps {
    item: FrontFeaturedContentUnavailableItem;
}

const UnavailableOriginalCard: React.FC<UnavailableOriginalCardProps> = ({
    item,
}) => {
    const kind = item.kind === "film" ? "Film" : "Series";

    return (
        <article
            className="front-originals__card front-originals__card--unavailable"
            aria-label={`Featured ${kind.toLowerCase()} is temporarily unavailable`}
        >
            <span className="front-originals__shade" aria-hidden="true" />

            <span className="front-originals__card-copy">
                <span className="front-originals__meta">
                    <span>Featured selection</span>
                    <span>{kind}</span>
                </span>

                <span
                    className="front-originals__title"
                    role="heading"
                    aria-level={3}
                >
                    Temporarily unavailable
                </span>

                <span className="front-originals__description">
                    This featured {kind.toLowerCase()} couldn&apos;t be loaded
                    from the catalogue right now.
                </span>
            </span>
        </article>
    );
};

const FrontOriginalsSection: React.FC = () => {
    const cta = useFrontPageCta("Browse the full library", RoutePaths.Browse);
    const { items, loading, error } = useFetchFrontFeaturedContent();

    return (
        <section
            id="originals"
            className="front-originals"
            aria-labelledby="front-originals-heading"
            aria-busy={loading}
            tabIndex={-1}
        >
            <div className="front-originals__inner">
                <div className="front-originals__intro">
                    <div>
                        <p className="front-originals__kicker">
                            Flagship viewing
                        </p>
                        <h2 id="front-originals-heading">
                            Different voices.
                            <span>Deeper questions.</span>
                        </h2>
                    </div>

                    <div className="front-originals__intro-copy">
                        <p>
                            From weekly current affairs to original
                            investigations, explore films and series that follow
                            their subject beyond the headline and trust you with
                            complexity instead of prescribing a conclusion.
                        </p>
                        <Link to={cta.link}>
                            {cta.text}
                            <span aria-hidden="true">&rarr;</span>
                        </Link>
                    </div>
                </div>

                {loading ? (
                    <div className="front-originals__loading" role="status">
                        <span className="front-originals__status-copy">
                            Loading featured films and series&hellip;
                        </span>
                        {[0, 1, 2].map((index) => (
                            <span
                                className="front-originals__loading-card"
                                aria-hidden="true"
                                key={index}
                            />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <p className="front-originals__empty" role="status">
                        {error ||
                            "Featured films and series are unavailable right now."}
                    </p>
                ) : (
                    <>
                        {error ? (
                            <span
                                className="front-originals__status-copy"
                                role="status"
                            >
                                {error}
                            </span>
                        ) : null}
                        <div className="front-originals__collection">
                            {items.map((item: FrontFeaturedContentItem) =>
                                isFrontFeaturedContentAvailable(item) ? (
                                    <OriginalCard
                                        item={item}
                                        key={item.slot}
                                    />
                                ) : (
                                    <UnavailableOriginalCard
                                        item={item}
                                        key={item.slot}
                                    />
                                )
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default FrontOriginalsSection;
